"use client";

import VehicleDetailEcoIcon from "@/app/components/icons/vehicle-detail-eco";
import {
  EfficiencyLabel,
  isValidEfficiencyLabel,
} from "@/app/components/offers/efficiency-label";
import { Printer } from "lucide-react";
import type { ReactNode } from "react";
import type { OfferFormDataT } from "@/app/types/offers";
import {
  formatPriceWithFallback,
  formatFuelConsumption,
  formatCo2Emission,
  formatElectricRange,
  formatElectricConsumption,
  formatHybridConsumptionCombined,
} from "./utils/formatters";

const hasMeaningfulValue = (value: number | null | undefined): boolean =>
  value != null && value > 0;

const hasMeaningfulKraftstoffData = (
  kraftstoff: NonNullable<OfferFormDataT["consumption"]>["kraftstoff"]
): boolean =>
  !!kraftstoff &&
  (hasMeaningfulValue(kraftstoff.verbrauchKombiniert) ||
    kraftstoff.co2Emissionen != null);

const hasMeaningfulElektroData = (
  elektro: NonNullable<OfferFormDataT["consumption"]>["elektro"]
): boolean =>
  !!elektro &&
  (hasMeaningfulValue(elektro.stromverbrauchKombiniert) ||
    hasMeaningfulValue(elektro.elektrischeReichweite));

const hasMeaningfulHybridData = (
  hybrid: NonNullable<OfferFormDataT["consumption"]>["hybrid"]
): boolean => {
  if (!hybrid) return false;
  const hasStrom =
    hasMeaningfulValue(hybrid.stromverbrauchGewichtetKombiniert) ||
    hasMeaningfulValue(hybrid.stromverbrauchKombiniert);
  const hasVerbrauch =
    hasMeaningfulValue(hybrid.verbrauchGewichtetKombiniert) ||
    hasMeaningfulValue(hybrid.verbrauchKombiniert) ||
    hasMeaningfulValue(hybrid.verbrauchKombiniertBatterieEntladen);
  const hasCo2 =
    hybrid.co2EmissionenGewichtetKombiniert != null ||
    hybrid.co2EmissionenBatterieEntladen != null;
  return hasStrom || hasVerbrauch || hasCo2;
};

type VehicleConsumptionDetailsPropsT = {
  consumption: OfferFormDataT["consumption"];
  consumptionFileUrl?: string | null;
  co2Costs?: {
    kraftfahrzeugsteuer?: number | null;
    kraftstoff?: {
      co2Kosten15000km?: number | null;
      co2PreisNiedrig?: number | null;
      co2PreisMittel?: number | null;
      co2PreisHoch?: number | null;
    };
    hybrid?: {
      co2PreisNiedrig?: number | null;
      co2PreisMittel?: number | null;
      co2PreisHoch?: number | null;
    };
  };
};

function ConsumptionDetailRow({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-0.5 py-0.5">
      <div className="min-w-44 flex-1 text-sm leading-snug">{label}</div>
      <div className="ml-auto max-w-full text-right leading-snug font-semibold">
        {children}
      </div>
    </div>
  );
}

/**
 * VehicleConsumptionDetails
 *
 * Renders consumption and emission details for a vehicle.
 * Displays fuel consumption, CO₂ emissions, energy costs, and related information.
 * Supports different fuel types: gasoline, diesel, hybrid, and electric.
 */
export default function VehicleConsumptionDetails({
  consumption,
  consumptionFileUrl,
  co2Costs,
}: VehicleConsumptionDetailsPropsT) {
  if (!consumption) {
    return null;
  }

  const consumptionType = consumption.type;

  // Render based on consumption type
  switch (consumptionType) {
    case "gasoline":
    case "diesel":
    case "truck":
      return renderKraftstoffConsumption(
        consumption,
        consumptionFileUrl,
        co2Costs
      );
    case "hybrid":
      return renderHybridConsumption(consumption, consumptionFileUrl, co2Costs);
    case "electric":
      return renderElectricConsumption(
        consumption,
        consumptionFileUrl,
        co2Costs
      );
    default:
      return null;
  }
}

/**
 * Render consumption details for gasoline/diesel vehicles
 */
function renderKraftstoffConsumption(
  consumption: OfferFormDataT["consumption"],
  consumptionFileUrl?: string | null,
  co2Costs?: VehicleConsumptionDetailsPropsT["co2Costs"]
) {
  const kraftstoff = consumption?.kraftstoff;
  if (!kraftstoff || !hasMeaningfulKraftstoffData(kraftstoff)) {
    return null;
  }

  return (
    <div id="vehicle-emissions" className="pt-8">
      <div className="flex flex-row items-center gap-2 font-bold">
        <span>
          <VehicleDetailEcoIcon />
        </span>
        Energieverbrauch und Emission
      </div>

      <div id="consumption" className="border-b pt-2 pb-4">
        <ConsumptionDetailRow label="Verbrauch (kombiniert):">
          {formatFuelConsumption(kraftstoff.verbrauchKombiniert)}
        </ConsumptionDetailRow>
        <ConsumptionDetailRow label="CO₂-Emissionen:">
          {formatCo2Emission(kraftstoff.co2Emissionen)}
        </ConsumptionDetailRow>
        <ConsumptionDetailRow label="Euro-Norm:">
          {consumption.euroNorm || "-"}
        </ConsumptionDetailRow>
      </div>

      <div className="border-b pt-5 pb-5 lg:grid lg:grid-cols-2 lg:gap-4">
        <div className="flex flex-col gap-5 border-b pb-4 text-sm lg:border-r lg:border-b-0 lg:pt-0 lg:pb-0">
          {kraftstoff.co2Label &&
            isValidEfficiencyLabel(kraftstoff.co2Label) && (
              <EfficiencyLabel label={kraftstoff.co2Label} />
            )}

          {consumptionFileUrl && (
            <div className="flex flex-row items-center gap-1.5">
              <Printer
                className="text-primary h-6 w-6 flex-shrink-0"
                strokeWidth={1.2}
              />
              <a
                href={consumptionFileUrl}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-primary text-sm font-normal underline decoration-[1.5px]"
              >
                Information über den Energieverbrauch und CO₂-Emissionen
                herunterladen
              </a>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 pt-4 lg:pt-0">
          <div>
            <div className="pb-0.5 font-semibold">Kraftstoffverbrauch</div>
            <ConsumptionDetailRow label="Kombiniert:">
              {formatFuelConsumption(kraftstoff.verbrauchKombiniert)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Stadt:">
              {formatFuelConsumption(kraftstoff.verbrauchStadt)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Stadtrand:">
              {formatFuelConsumption(kraftstoff.verbrauchStadtrand)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Landstrasse:">
              {formatFuelConsumption(kraftstoff.verbrauchLandstrasse)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Autobahn:">
              {formatFuelConsumption(kraftstoff.verbrauchAutobahn)}
            </ConsumptionDetailRow>
          </div>
        </div>
      </div>

      {co2Costs && (
        <>
          <div className="border-b pt-4 pb-4">
            <ConsumptionDetailRow label="Kraftfahrzeugsteuer:">
              {formatPriceWithFallback(co2Costs.kraftfahrzeugsteuer ?? null)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Energiekosten pro Jahr (bei 15.000 km / Jahr):">
              {formatPriceWithFallback(
                co2Costs.kraftstoff?.co2Kosten15000km ?? null
              )}
            </ConsumptionDetailRow>
          </div>

          <div className="pt-4 pb-4">
            <ConsumptionDetailRow label="Niedriger CO₂-Preis:">
              {formatPriceWithFallback(
                co2Costs.kraftstoff?.co2PreisNiedrig ?? null
              )}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Mittlerer CO₂-Preis:">
              {formatPriceWithFallback(
                co2Costs.kraftstoff?.co2PreisMittel ?? null
              )}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Hoher CO₂-Preis:">
              {formatPriceWithFallback(
                co2Costs.kraftstoff?.co2PreisHoch ?? null
              )}
            </ConsumptionDetailRow>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Render consumption details for hybrid vehicles
 */
function renderHybridConsumption(
  consumption: OfferFormDataT["consumption"],
  consumptionFileUrl?: string | null,
  co2Costs?: VehicleConsumptionDetailsPropsT["co2Costs"]
) {
  const hybrid = consumption?.hybrid;
  if (!hybrid || !hasMeaningfulHybridData(hybrid)) {
    return null;
  }

  return (
    <div id="vehicle-emissions" className="pt-8">
      <div className="flex flex-row items-center gap-2 font-bold">
        <span>
          <VehicleDetailEcoIcon />
        </span>
        Energieverbrauch und Emission
      </div>

      <div id="consumption" className="border-b pt-2 pb-4">
        <ConsumptionDetailRow label="Verbrauch (gewichtet, kombiniert):">
          {formatHybridConsumptionCombined(
            hybrid.stromverbrauchGewichtetKombiniert ??
              hybrid.stromverbrauchKombiniert,
            hybrid.verbrauchGewichtetKombiniert
          )}
        </ConsumptionDetailRow>
        <ConsumptionDetailRow label="CO₂-Emissionen (gewichtet, kombiniert):">
          {formatCo2Emission(hybrid.co2EmissionenGewichtetKombiniert)}
        </ConsumptionDetailRow>
        <ConsumptionDetailRow label="Kraftstoffverbrauch bei entladener Batterie (kombiniert):">
          {formatFuelConsumption(
            hybrid.verbrauchKombiniertBatterieEntladen ??
              hybrid.verbrauchKombiniert
          )}
        </ConsumptionDetailRow>
        <ConsumptionDetailRow label="CO₂-Emissionen bei entladener Batterie (kombiniert):">
          {hybrid.co2EmissionenBatterieEntladen !== null &&
          hybrid.co2EmissionenBatterieEntladen !== undefined
            ? formatCo2Emission(hybrid.co2EmissionenBatterieEntladen)
            : "keine Angabe"}
        </ConsumptionDetailRow>
        <ConsumptionDetailRow label="Euro-Norm:">
          {consumption.euroNorm || "-"}
        </ConsumptionDetailRow>
        {consumption.elektro?.elektrischeReichweite && (
          <ConsumptionDetailRow label="Elektrische Reichweite:">
            {formatElectricRange(consumption.elektro.elektrischeReichweite)}
          </ConsumptionDetailRow>
        )}
      </div>

      <div className="border-b pt-5 pb-5 lg:grid lg:grid-cols-2 lg:gap-4">
        <div className="flex flex-col gap-5 border-b pb-4 text-sm lg:border-r lg:border-b-0 lg:pt-0 lg:pb-0">
          <div>
            <div className="pb-2 font-semibold">CO₂-Klasse</div>
            {hybrid.co2LabelGewichtetKombiniert &&
              isValidEfficiencyLabel(hybrid.co2LabelGewichtetKombiniert) && (
                <div className="flex flex-col gap-1">
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>gewichtet, kombiniert</div>
                    {hybrid.co2LabelBatterieEntladen &&
                      isValidEfficiencyLabel(
                        hybrid.co2LabelBatterieEntladen
                      ) && <div>bei entladener Batterie</div>}
                  </div>
                  <EfficiencyLabel
                    label={hybrid.co2LabelGewichtetKombiniert}
                    secondLabel={
                      hybrid.co2LabelBatterieEntladen &&
                      isValidEfficiencyLabel(hybrid.co2LabelBatterieEntladen)
                        ? hybrid.co2LabelBatterieEntladen
                        : undefined
                    }
                  />
                </div>
              )}
          </div>
          {consumptionFileUrl && (
            <div className="flex flex-row items-center gap-1.5">
              <Printer
                className="text-primary h-6 w-6 flex-shrink-0"
                strokeWidth={1.2}
              />
              <a
                href={consumptionFileUrl}
                download
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-primary text-sm font-normal underline decoration-[1.5px]"
              >
                Information über den Energieverbrauch und CO₂-Emissionen
                herunterladen
              </a>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 pt-4 lg:pt-0">
          <div>
            <div className="pb-0.5 font-semibold">
              Kraftstoffverbrauch bei entladener Batterie
            </div>
            <ConsumptionDetailRow label="Kombiniert:">
              {formatFuelConsumption(
                hybrid.verbrauchKombiniertBatterieEntladen ??
                  hybrid.verbrauchKombiniert
              )}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Stadt:">
              {formatFuelConsumption(hybrid.verbrauchEntladenStadt)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Stadtrand:">
              {formatFuelConsumption(hybrid.verbrauchEntladenStadtrand)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Landstrasse:">
              {formatFuelConsumption(hybrid.verbrauchEntladenLandstrasse)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Autobahn:">
              {formatFuelConsumption(hybrid.verbrauchEntladenAutobahn)}
            </ConsumptionDetailRow>
          </div>
          <div>
            <div className="pb-0.5 font-semibold">
              Stromverbrauch bei rein elektrischem Betrieb
            </div>
            <ConsumptionDetailRow label="Kombiniert:">
              {formatElectricConsumption(hybrid.stromverbrauchKombiniert)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Stadt:">
              {formatElectricConsumption(hybrid.stromverbrauchStadt)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Stadtrand:">
              {formatElectricConsumption(hybrid.stromverbrauchStadtrand)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Landstrasse:">
              {formatElectricConsumption(hybrid.stromverbrauchLandstrasse)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Autobahn:">
              {formatElectricConsumption(hybrid.stromverbrauchAutobahn)}
            </ConsumptionDetailRow>
          </div>
        </div>
      </div>

      <div className="border-b pt-4 pb-4">
        {co2Costs && (
          <ConsumptionDetailRow label="Kraftfahrzeugsteuer:">
            {formatPriceWithFallback(co2Costs.kraftfahrzeugsteuer ?? null)}
          </ConsumptionDetailRow>
        )}
        <ConsumptionDetailRow label="Energiekosten pro Jahr:">
          {formatPriceWithFallback(hybrid.energiekostenProJahr)}
        </ConsumptionDetailRow>
      </div>

      {co2Costs && (
        <div className="pt-4 pb-4">
          <ConsumptionDetailRow label="Niedriger CO₂-Preis:">
            {formatPriceWithFallback(co2Costs.hybrid?.co2PreisNiedrig ?? null)}
          </ConsumptionDetailRow>
          <ConsumptionDetailRow label="Mittlerer CO₂-Preis:">
            {formatPriceWithFallback(co2Costs.hybrid?.co2PreisMittel ?? null)}
          </ConsumptionDetailRow>
          <ConsumptionDetailRow label="Hoher CO₂-Preis:">
            {formatPriceWithFallback(co2Costs.hybrid?.co2PreisHoch ?? null)}
          </ConsumptionDetailRow>
        </div>
      )}
    </div>
  );
}

/**
 * Render consumption details for electric vehicles
 */
function renderElectricConsumption(
  consumption: OfferFormDataT["consumption"],
  consumptionFileUrl?: string | null,
  co2Costs?: VehicleConsumptionDetailsPropsT["co2Costs"]
) {
  const elektro = consumption?.elektro;
  if (!elektro || !hasMeaningfulElektroData(elektro)) {
    return null;
  }

  return (
    <div id="vehicle-emissions" className="pt-8">
      <div className="flex flex-row items-center gap-2 font-bold">
        <span>
          <VehicleDetailEcoIcon />
        </span>
        Energieverbrauch und Emission
      </div>

      <div id="consumption" className="border-b pt-2 pb-4">
        <ConsumptionDetailRow label="Stromverbrauch (kombiniert):">
          {formatElectricConsumption(elektro.stromverbrauchKombiniert)}
        </ConsumptionDetailRow>
        <ConsumptionDetailRow label="CO₂-Emissionen:">
          0 g/km
        </ConsumptionDetailRow>
        <ConsumptionDetailRow label="Elektrische Reichweite:">
          {formatElectricRange(elektro.elektrischeReichweite)}
        </ConsumptionDetailRow>
      </div>

      <div className="border-b pt-5 pb-5 lg:grid lg:grid-cols-2 lg:gap-4">
        <div className="flex flex-col gap-5 border-b pb-4 text-sm lg:border-r lg:border-b-0 lg:pt-0 lg:pb-0">
          {elektro.energielabel &&
            isValidEfficiencyLabel(elektro.energielabel) && (
              <EfficiencyLabel label={elektro.energielabel} />
            )}
          {consumptionFileUrl && (
            <div className="flex flex-row items-center gap-1.5">
              <Printer
                className="text-primary h-6 w-6 flex-shrink-0"
                strokeWidth={1.2}
              />
              <a
                href={consumptionFileUrl}
                download
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-primary text-sm font-normal underline decoration-[1.5px]"
              >
                Information über den Energieverbrauch und CO₂-Emissionen
                herunterladen
              </a>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 pt-4 lg:pt-0">
          <div>
            <div className="pb-0.5 font-semibold">Stromverbrauch</div>
            <ConsumptionDetailRow label="Kombiniert:">
              {formatElectricConsumption(elektro.stromverbrauchKombiniert)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Stadt:">
              {formatElectricConsumption(elektro.stromverbrauchStadt)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Stadtrand:">
              {formatElectricConsumption(elektro.stromverbrauchStadtrand)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Landstrasse:">
              {formatElectricConsumption(elektro.stromverbrauchLandstrasse)}
            </ConsumptionDetailRow>
            <ConsumptionDetailRow label="Autobahn:">
              {formatElectricConsumption(elektro.stromverbrauchAutobahn)}
            </ConsumptionDetailRow>
          </div>
        </div>
      </div>

      <div className="border-b pt-4 pb-4">
        {co2Costs && (
          <ConsumptionDetailRow label="Kraftfahrzeugsteuer:">
            {formatPriceWithFallback(co2Costs.kraftfahrzeugsteuer ?? null)}
          </ConsumptionDetailRow>
        )}
        <ConsumptionDetailRow label="Energiekosten pro Jahr:">
          {formatPriceWithFallback(elektro.energiekostenProJahr)}
        </ConsumptionDetailRow>
      </div>

      {co2Costs && (
        <div className="pt-4 pb-4">
          <ConsumptionDetailRow label="Niedriger CO₂-Preis:">
            0,00 €
          </ConsumptionDetailRow>
          <ConsumptionDetailRow label="Mittlerer CO₂-Preis:">
            0,00 €
          </ConsumptionDetailRow>
          <ConsumptionDetailRow label="Hoher CO₂-Preis:">
            0,00 €
          </ConsumptionDetailRow>
        </div>
      )}
    </div>
  );
}
